// The pre-leads table (desktop): a click on a row opens the drawer, the ticks open the bulk bar.
import { Loader2 } from "lucide-react";
import { useTranslate, type Identifier } from "ra-core";
import { useContext, useState } from "react";
import { DataTable } from "@/components/admin/data-table";
import { cn } from "@/lib/utils";

import { PreLeadBulkActions } from "./PreLeadBulkActions";
import { PreLeadBusyContext } from "./preLeadBusy";
import {
  nextTextFor,
  statusLabel,
  statusTone,
  TONE_CLASSES,
} from "./preLeadDisplay";
import type { PreLead } from "./types";

export const PreLeadStatusTag = ({ preLead }: { preLead: PreLead }) => {
  const translate = useTranslate();
  const { busy } = useContext(PreLeadBusyContext);
  return (
    <span className="inline-flex items-center gap-1">
      {busy.has(preLead.id) && (
        <Loader2 className="size-3 animate-spin" aria-hidden />
      )}
      <span
        className={cn(
          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap",
          TONE_CLASSES[statusTone(preLead)],
        )}
      >
        {statusLabel(preLead, translate)}
      </span>
    </span>
  );
};

export const PreLeadNext = ({ preLead }: { preLead: PreLead }) => {
  const translate = useTranslate();
  return (
    <span
      className={cn(
        "text-sm text-muted-foreground",
        preLead.status === "replied" &&
          preLead.needs_reply &&
          "font-semibold text-foreground",
      )}
    >
      {nextTextFor(preLead, translate)}
    </span>
  );
};

const Sub = ({ parts }: { parts: (string | number | null | undefined)[] }) => {
  const text = parts.filter((p) => p != null && p !== "").join(" · ");
  return text ? (
    <span className="block text-xs text-muted-foreground">{text}</span>
  ) : null;
};

const place = (p: PreLead) =>
  [p.city, p.country].filter(Boolean).join(", ") || null;

export const PreLeadTable = ({
  onOpen,
  currentId,
  compact,
}: {
  onOpen: (preLead: PreLead) => void;
  /** the row the keyboard and the drawer are on */
  currentId?: Identifier | null;
  /** the wide drawer is open: fold to Company, Fit and Status */
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const folded = compact ? "hidden" : undefined;
  const [busy, setBusy] = useState<Set<Identifier>>(new Set());
  return (
    <PreLeadBusyContext.Provider value={{ busy, setBusy }}>
      <DataTable<PreLead>
        rowClick={(_id, _resource, record) => {
          onOpen(record);
          return false;
        }}
        bulkActionButtons={<PreLeadBulkActions />}
        rowClassName={(p) => (p.id === currentId ? "bg-muted" : undefined)}
      >
        <DataTable.Col<PreLead>
          source="company_name"
          render={(p) => (
            <>
              <span className="font-semibold">{p.company_name}</span>
              <Sub
                parts={[
                  p.sector ?? p.industry,
                  place(p),
                  p.employees?.toLocaleString(),
                ]}
              />
            </>
          )}
        />
        <DataTable.Col<PreLead>
          source="contact_email"
          className={folded}
          disableSort
          render={(p) => (
            <>
              <span>
                {[p.contact_first_name, p.contact_last_name]
                  .filter(Boolean)
                  .join(" ") || p.contact_email}
              </span>
              <Sub parts={[p.contact_title, p.sender]} />
            </>
          )}
        />
        <DataTable.Col<PreLead>
          source="fit"
          render={(p) =>
            p.fit == null ? null : (
              <span className="inline-flex items-center gap-2">
                <span className="font-semibold tabular-nums">{p.fit}%</span>
                <span className="h-1.5 w-12 rounded-full bg-muted">
                  <span
                    className="block h-full rounded-full bg-primary"
                    style={{ width: `${Math.min(100, Math.max(0, p.fit))}%` }}
                  />
                </span>
              </span>
            )
          }
        />
        <DataTable.Col<PreLead>
          source="why_now"
          className={folded}
          disableSort
          render={(p) =>
            p.why_now ?? (
              <span className="text-muted-foreground">
                {translate("resources.pre_leads.none_found")}
              </span>
            )
          }
        />
        <DataTable.Col<PreLead>
          source="status"
          render={(p) => <PreLeadStatusTag preLead={p} />}
        />
        <DataTable.Col<PreLead>
          source="next_touch_at"
          className={folded}
          render={(p) => <PreLeadNext preLead={p} />}
        />
      </DataTable>
    </PreLeadBusyContext.Provider>
  );
};
